import React, { ReactNode, useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';

import { getInLocalStorage, setInLocalStorage } from '@trqk/utils';
import { queryStrings } from '@trqk/resolvers';
import { useGetDirectoryVersionLazyQuery } from '@trqk/api';

import {
    useModal,
    useUploadAudioFiles,
    useUploadStatements,
} from '../../../hooks';
import { AppVersionStatusContent } from '.';
import { localStorageKeys } from './constants';

const { GET_SESSION_IS_VALID } = queryStrings;

interface AppVersionStatusProps {
    children: ReactNode;
}

const ONE_HOUR = 1000 * 60 * 60;

/**
 * Checks whether there is a new version of the app available. If so, opens a modal that prompts
 * the user to refresh their browser.
 * @param props - see {@link AppVersionStatusProps}
 */
export const AppVersionStatus = ({
    children,
}: AppVersionStatusProps): JSX.Element => {
    const modal = useModal();
    const sessionQuery =
        useQuery<{ session: { sessionIsValid: boolean } }>(
            GET_SESSION_IS_VALID,
        );
    const sessionIsValid = sessionQuery?.data?.session?.sessionIsValid ?? false;
    const [didSaveVersionOnLoad, setDidSaveVersionOnLoad] = useState(false);
    const [foundVersionMismatch, setFoundVersionMismatch] = useState(false);

    const [getDirectoryVersion] = useGetDirectoryVersionLazyQuery({
        pollInterval: ONE_HOUR,
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: true,
        onCompleted: data => {
            const directoryVersion = data?.getDirectoryVersion ?? null;

            if (directoryVersion !== null) {
                if (!didSaveVersionOnLoad) {
                    setInLocalStorage<number>(
                        localStorageKeys.directoryVersionOnAppLoad,
                        () => directoryVersion,
                    );

                    setDidSaveVersionOnLoad(true);
                }

                const cachedVersion = getInLocalStorage<number>(
                    localStorageKeys.directoryVersionOnAppLoad,
                );

                if (cachedVersion !== null) {
                    if (directoryVersion !== cachedVersion) {
                        setFoundVersionMismatch(true);
                    }
                }
            }
        },
    });

    useEffect(() => {
        if (sessionIsValid) {
            getDirectoryVersion();
        }
    }, [sessionIsValid]);

    const { hasActiveUploads } = useUploadStatements();
    const { hasActiveAudioUploads } = useUploadAudioFiles();

    useEffect(() => {
        // When the web app is out of date and the user does not have any active statement
        // ingestions or audio uploads, prompt them to refresh the browser.
        if (
            foundVersionMismatch &&
            !hasActiveUploads &&
            !hasActiveAudioUploads
        ) {
            modal.add({
                Content: AppVersionStatusContent,
            });
        }
    }, [foundVersionMismatch, hasActiveUploads, hasActiveAudioUploads]);

    return <>{children}</>;
};
